import React from 'react';
import { DashboardWidget } from '../../services/dashboardConfigurationService';

interface WidgetPreviewProps {
  widget: DashboardWidget;
  onRemove: (widgetId: number) => void;
  onEdit: (widget: DashboardWidget) => void;
  isDragging?: boolean;
}

const WidgetPreview: React.FC<WidgetPreviewProps> = ({ 
  widget, 
  onRemove, 
  onEdit, 
  isDragging = false 
}) => {
  const getWidgetIcon = () => {
    switch (widget.widgetType) {
      case 'chart':
        return '📊';
      case 'metric_card':
        return '📈';
      case 'table':
        return '📋';
      case 'insight_card':
        return '💡';
      case 'kpi_summary':
        return '🎯';
      default:
        return '📊';
    }
  };

  const getChartTypeLabel = () => {
    if (widget.widgetType !== 'chart' || !widget.chartType) return '';
    
    const chartLabels: { [key: string]: string } = {
      'bar': 'Bar Chart',
      'line': 'Line Chart',
      'pie': 'Pie Chart',
      'doughnut': 'Doughnut Chart',
      'area': 'Area Chart',
      'scatter': 'Scatter Plot',
      'radar': 'Radar Chart',
      'bubble': 'Bubble Chart',
      'horizontalBar': 'Horizontal Bar'
    };
    
    return chartLabels[widget.chartType] || widget.chartType;
  };

  return (
    <div 
      className={`widget-preview ${isDragging ? 'dragging' : ''}`}
      style={{
        gridColumnStart: widget.positionX + 1,
        gridColumnEnd: widget.positionX + widget.width + 1,
        gridRowStart: widget.positionY + 1,
        gridRowEnd: widget.positionY + widget.height + 1,
      }}
    >
      <div className="widget-header">
        <div className="widget-title">
          <span className="widget-icon">{getWidgetIcon()}</span>
          <span className="widget-name">{widget.widgetName}</span>
        </div>
        <div className="widget-actions">
          <button 
            className="widget-action-btn edit-btn"
            onClick={() => onEdit(widget)}
            title="Edit Widget"
          >
            ✏️
          </button>
          <button 
            className="widget-action-btn remove-btn"
            onClick={() => onRemove(widget.id || 0)}
            title="Remove Widget"
          >
            ✕
          </button>
        </div>
      </div>
      
      <div className="widget-content">
        <div className="widget-type-label">
          {widget.widgetType === 'chart' ? getChartTypeLabel() : widget.widgetType.replace('_', ' ').toUpperCase()}
        </div>
        
        {widget.dataConfig?.kpiCodes && widget.dataConfig.kpiCodes.length > 0 && (
          <div className="widget-kpis">
            <small>KPIs: {widget.dataConfig.kpiCodes.join(', ')}</small>
          </div>
        )}
        
        {widget.dataConfig?.calculationType && (
          <div className="widget-calculation">
            <small>Calc: {widget.dataConfig.calculationType}</small>
          </div>
        )}
        
        <div className="widget-preview-placeholder">
          <div className="preview-chart-mock">
            {widget.widgetType === 'chart' && (
              <div className="mock-chart-bars">
                <div className="mock-bar" style={{ height: '60%' }}></div>
                <div className="mock-bar" style={{ height: '80%' }}></div>
                <div className="mock-bar" style={{ height: '40%' }}></div>
                <div className="mock-bar" style={{ height: '90%' }}></div>
              </div>
            )}
            {widget.widgetType === 'metric_card' && (
              <div className="mock-metric">
                <div className="mock-number">42.5K</div>
                <div className="mock-label">Sample Metric</div>
              </div>
            )}
            {widget.widgetType === 'table' && (
              <div className="mock-table">
                <div className="mock-row header">
                  <div className="mock-cell"></div>
                  <div className="mock-cell"></div>
                </div>
                <div className="mock-row">
                  <div className="mock-cell"></div>
                  <div className="mock-cell"></div>
                </div>
                <div className="mock-row">
                  <div className="mock-cell"></div>
                  <div className="mock-cell"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetPreview;
